import FetchClient, { type ApiResponse } from "./FetchClient";

export interface ApiKey {
  apiKey: string;
  userId: string;
  active: boolean;
  created: string;
}

// Server response structure for API key operations
interface ServerApiKeyResponse {
  apiKey: string;
  created: number;
  lastUsed?: number;
  active: boolean;
}

interface ServerGenerateApiKeyResponse {
  apiKey: string;
  created: number;
  message: string;
}

// Get user's API key
export async function getApiKey(): Promise<ApiKey | null> {
  try {
    const response = await FetchClient.get<ApiResponse<ServerApiKeyResponse>>(
      "/api-keys"
    );

    // Transform server response to match frontend interface
    const serverData = response.data;
    const transformedApiKey: ApiKey = {
      apiKey: serverData.apiKey,
      userId: "", // Server doesn't provide userId, we'll get it from auth context
      active: serverData.active,
      created: new Date(serverData.created).toISOString(),
    };

    return transformedApiKey;
  } catch (error) {
    console.error("Error fetching API key:", error);
    // If no API key exists, return null instead of throwing
    if (
      error &&
      typeof error === "object" &&
      "status" in error &&
      (error as { status: number }).status === 404
    ) {
      return null;
    }
    throw error;
  }
}

// Generate a new API key
export async function generateApiKey(): Promise<ApiKey> {
  try {
    const response = await FetchClient.post<
      ApiResponse<ServerGenerateApiKeyResponse>
    >("/api-keys", {});

    // Transform server response to match frontend interface
    const serverData = response.data;
    const transformedApiKey: ApiKey = {
      apiKey: serverData.apiKey,
      userId: "", // Server doesn't provide userId, we'll get it from auth context
      active: true, // New keys are always active
      created: new Date(serverData.created).toISOString(),
    };

    return transformedApiKey;
  } catch (error) {
    console.error("Error generating API key:", error);
    throw error;
  }
}

// Regenerate an existing API key
export async function regenerateApiKey(): Promise<ApiKey> {
  try {
    const response = await FetchClient.post<
      ApiResponse<ServerGenerateApiKeyResponse>
    >("/api-keys/regenerate", {});

    // Transform server response to match frontend interface
    const serverData = response.data;
    const transformedApiKey: ApiKey = {
      apiKey: serverData.apiKey,
      userId: "", // Server doesn't provide userId, we'll get it from auth context
      active: true, // New keys are always active
      created: new Date(serverData.created).toISOString(),
    };

    return transformedApiKey;
  } catch (error) {
    console.error("Error regenerating API key:", error);
    throw error;
  }
}

// Deactivate API key
export async function deactivateApiKey(): Promise<void> {
  try {
    await FetchClient.post<ApiResponse<null>>("/api-keys/deactivate", {});
  } catch (error) {
    console.error("Error deactivating API key:", error);
    throw error;
  }
}
